import React, { useEffect, useState } from 'react';
import { Images, PermissionList, RolePermission } from 'utils';
import { AddCircleBlue, DateRow, EditWithIcon, StatusCard, TextRow } from 'components';

import { renderSessionAverage } from '../goalCharts/constants';

export const baselineStatusEnums = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
};

export const baselineTableInfo = (selectedIndex, handleAction, goalById, canCollectData) => {
    return {
        loadingType: 'GET_BASELINES',
        head: [
            { name: 'name', title: 'Baseline Name', width: '14%' },
            { name: 'created', title: 'Created Date', disabled: true, custom: false, width: '14%' },
            { name: 'modified', title: 'Last Modified', width: '14%', custom: false },
            { name: 'staff', title: 'Staff', width: '15%', custom: false },
            { name: 'summary', title: 'Summary', width: '10%', custom: false },
            { name: 'trials', title: 'Total Sessions', width: '11%', custom: false },
            {
                name: 'status',
                title: 'Status',
                disabled: true,
                width: '10%',
                filterList: [
                    { name: 'All', id: 'All' },
                    { name: 'Completed', id: baselineStatusEnums.ACTIVE },
                    { name: 'Closed', id: baselineStatusEnums.INACTIVE },
                ],
            },
            { name: 'action', title: 'Action', custom: false, disabled: true, width: '12%' },
        ],

        body: (item, index) => {
            return [
                { value: <TextRow name={item?.name} />, width: '14%' },
                { value: <DateRow date={item?.createdAt} />, width: '14%' },
                { value: <DateRow date={item?.updatedAt} />, width: '14%' },
                { value: <TextRow name={item?.creator?.fullName} />, width: '15%' },
                {
                    value: renderSessionAverage({
                        ...item,
                        dataType: goalById?.dataType,
                        value: item?.summary?.value,
                        data: item?.summary,
                    }),
                    width: '10%',
                },
                // { value: <div>{item?.summary?.value?.toFixed(2) || 0}%</div>, width: '10%' },
                { value: <TextRow name={item?.sessions?.length} />, width: '11%' },
                { value: <StatusCard status={item?.status} />, width: '10%' },
                {
                    value: canCollectData ? (
                        <div className="flex-align-center" style={{ gap: 8 }}>
                            <EditWithIcon icon={Images.edit} type={'button'} handleClick={() => handleAction('edit', item)} />
                        </div>
                    ) : (
                        ''
                    ),
                    notClickable: true,
                    width: '12%',
                },
            ];
        },

        renderBodyHeaderAction: (i) => {
            return (
                <div className="space-between" style={{ marginBottom: 12 }}>
                    <p className="charge-rate-title">Charge Rates</p>
                    {/*{canCollectData && (*/}
                    {RolePermission([PermissionList.FS_SERVICE_CREATE?.code]) && (
                        <AddCircleBlue text={'Add Session'} handleClick={() => handleAction('createSession', i)} />
                    )}
                </div>
            );
        },

        renderAccordionRow: (i) => i?.sessions,

        accordionHead: [
            { title: 'Session', width: '35%' },
            { title: 'Total Trials', width: '30%' },
            { title: 'Progress', width: '30%' },
            { title: 'Action', width: '5%' },
        ],

        accordionBody: (i, key, parent) => {
            return [
                { value: <DateRow date={i?.sessionDate} />, width: '35%' },
                { value: <TextRow name={i?.trials?.length} />, width: '30%' },
                {
                    value: renderSessionAverage({ ...i, dataType: goalById?.dataType, data: i?.trialData }),
                    width: '10%',
                },
                {
                    value: canCollectData ? (
                        <div className="flex-align-center" style={{ gap: 12 }}>
                            <button className="reset-btn-no-paddings" onClick={() => handleAction('editTrial', i, parent)}>
                                <img src={Images.edit} alt="icon" />
                            </button>
                            <button className="reset-btn-no-paddings" onClick={() => handleAction('deleteTrial', i, parent)}>
                                <img src={Images.redTrash} alt="icon" />
                            </button>
                        </div>
                    ) : (
                        ''
                    ),
                    width: '5%',
                },
            ];
        },

        noDetailsText: 'No trials yet',
        notYet: {
            title: 'No Baselines Yet',
            subTitle: 'There are no baselines.',
            image: Images.noPendingClaim,
        },
    };
};

export const HourMinuteSecondInput = ({ value = 0, onChange, error }) => {
    const [hours, setHours] = useState(Math.floor(value / 3600));
    const [minutes, setMinutes] = useState(Math.floor((value % 3600) / 60));
    const [seconds, setSeconds] = useState(value % 60);

    useEffect(() => {
        setHours(Math.floor(value / 3600));
        setMinutes(Math.floor((value % 3600) / 60));
        setSeconds(value % 60);
    }, [value]);

    const updateTime = (newHours, newMinutes, newSeconds) => {
        if (newHours < 0) newHours = 0;
        if (newHours > 99) newHours = 99; // ✅ limit to 99
        if (newMinutes < 0) newMinutes = 0;
        if (newMinutes > 59) newMinutes = 59;
        if (newSeconds < 0) newSeconds = 0;
        if (newSeconds > 59) newSeconds = 59;

        setHours(newHours);
        setMinutes(newMinutes);
        setSeconds(newSeconds);

        const totalSeconds = newHours * 3600 + newMinutes * 60 + newSeconds;
        onChange?.(totalSeconds);
    };

    return (
        <div
            className="baseline-trial-time-input-wrapper"
            style={{ display: 'flex', alignItems: 'center', gap: '5px', border: error ? '1px solid #F07379' : '1px solid #0C66E4' }}
        >
            <input
                type="number"
                min="0"
                max="99" // ✅ enforce max 99
                value={hours || ''}
                onChange={(e) => updateTime(parseInt(e.target.value || '0', 10), minutes, seconds)}
                placeholder="hh"
            />
            <p>:</p>
            <input
                type="number"
                min="0"
                max="59"
                value={minutes || ''}
                onChange={(e) => updateTime(hours, parseInt(e.target.value || '0', 10), seconds)}
                placeholder="mm"
            />
            <p>:</p>
            <input
                type="number"
                min="0"
                max="59"
                value={seconds || ''}
                onChange={(e) => updateTime(hours, minutes, parseInt(e.target.value || '0', 10))}
                placeholder="ss"
            />
        </div>
    );
};

export const evalModeEnums = {
    BY_DAY: 'BY_DAY',
    BY_SESSION: 'BY_SESSION',
};
